import { redirect } from "@tanstack/react-router"
import { createServerFn } from "@tanstack/react-start"
import { getRequestUrl } from "@tanstack/react-start/server"
import { z } from "zod"
import {
  createSsoTicket,
  getSisterAppUrl,
  parseSisterConsumeReturnTo,
  verifySsoTicket,
} from "@/auth/lib/sso-ticket.server"
import { updateAppSession, useAppSession } from "@/auth/session.server"
import { getUserId, isValidUserId } from "@/auth/user.server"
import { APP_HOME_PATH } from "../../app-surface"
import { getRedirectUrl } from "../auth.server"

export type SsoAction =
  | { kind: "none" }
  | { kind: "external-redirect"; url: string }
  | { kind: "internal-redirect"; to: string; search?: Record<string, string> }

/**
 * If a sister app is configured and SSO has not already failed, return an
 * external redirect URL to that app's /sso/issue endpoint.
 */
export const planSisterSso = createServerFn({ method: "GET" })
  .validator((data: unknown) =>
    z
      .object({
        redirectTo: z.string().optional(),
        ssoFailed: z.boolean().optional(),
      })
      .parse(data ?? {}),
  )
  .handler(async ({ data }): Promise<SsoAction> => {
    if (data.ssoFailed) return { kind: "none" }

    const sister = getSisterAppUrl()
    if (!sister) return { kind: "none" }

    const origin = getRequestUrl().origin
    const consume = new URL("/sso/consume", origin)
    consume.searchParams.set("redirectTo", data.redirectTo ?? APP_HOME_PATH)
    const issue = new URL("/sso/issue", sister)
    issue.searchParams.set("returnTo", consume.toString())
    return { kind: "external-redirect", url: issue.toString() }
  })

export const planIssueSsoTicket = createServerFn({ method: "GET" })
  .validator((data: unknown) =>
    z.object({ returnTo: z.string().min(1) }).parse(data),
  )
  .handler(async ({ data }): Promise<SsoAction> => {
    const consumeUrl = parseSisterConsumeReturnTo(data.returnTo)
    if (!consumeUrl) {
      return { kind: "internal-redirect", to: APP_HOME_PATH }
    }

    const session = await useAppSession()
    const userId = await getUserId(session)

    if (!userId || !(await isValidUserId(userId))) {
      const sisterLogin = new URL("/login", consumeUrl.origin)
      sisterLogin.searchParams.set("ssoFailed", "1")
      const next = consumeUrl.searchParams.get("redirectTo")
      if (next) sisterLogin.searchParams.set("redirectTo", next)
      return { kind: "external-redirect", url: sisterLogin.toString() }
    }

    const ticket = await createSsoTicket(userId)
    consumeUrl.searchParams.set("ticket", ticket)
    return { kind: "external-redirect", url: consumeUrl.toString() }
  })

export const consumeSsoTicketFn = createServerFn({ method: "GET" })
  .validator((data: unknown) =>
    z
      .object({
        ticket: z.string().optional(),
        redirectTo: z.string().optional(),
      })
      .parse(data ?? {}),
  )
  .handler(async ({ data }) => {
    const dest = getRedirectUrl(data.redirectTo ?? APP_HOME_PATH)

    if (!data.ticket) {
      throw redirect({
        to: "/login",
        search: { redirectTo: dest, ssoFailed: "1" },
      })
    }

    const verified = await verifySsoTicket(data.ticket)
    if (!verified || !(await isValidUserId(verified.userId))) {
      throw redirect({
        to: "/login",
        search: { redirectTo: dest, ssoFailed: "1" },
      })
    }

    await updateAppSession({ userId: verified.userId })
    throw redirect({ to: dest })
  })

/** Apply an SsoAction from a route beforeLoad. */
export function applySsoAction(action: SsoAction): void {
  if (action.kind === "none") return
  if (action.kind === "external-redirect") {
    // href supports absolute sister-app URLs (Workers cannot share cookies).
    throw redirect({ href: action.url })
  }
  throw redirect({
    href: action.search
      ? `${action.to}?${new URLSearchParams(action.search).toString()}`
      : action.to,
  })
}
